/**
 * Error boundary.
 *
 * Without one, a single render error unmounts the whole React tree and leaves
 * a blank screen. This catches such errors, shows what went wrong, and offers
 * a reload — so one misbehaving panel never blanks the entire app.
 */

import React from "react";

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  message: string;
}

export default class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error: unknown): State {
    const message = error instanceof Error ? error.message : String(error);
    return { hasError: true, message };
  }

  componentDidCatch(error: unknown, info: unknown): void {
    // Surface in the console for debugging.
    // eslint-disable-next-line no-console
    console.error("Render error caught by ErrorBoundary:", error, info);
  }

  private handleReload = (): void => {
    this.setState({ hasError: false, message: "" });
    window.location.reload();
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-ink-950 p-6">
          <div className="max-w-md rounded-xl border border-ink-700 bg-ink-900 p-6 text-center">
            <p className="text-lg font-semibold text-mist-100">Something hit a snag</p>
            <p className="mt-2 text-sm text-mist-400">
              A part of the page ran into an error and stopped rendering. Your data is safe.
            </p>
            {this.state.message && (
              <p className="mt-3 break-words rounded bg-ink-950 p-2 text-left font-mono text-xs text-rose-300">
                {this.state.message}
              </p>
            )}
            <button
              type="button"
              onClick={this.handleReload}
              className="mt-4 rounded-lg border border-signal-teal/40 bg-signal-teal/10 px-4 py-2 text-sm font-medium text-signal-teal transition hover:bg-signal-teal/20"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
